// src/components/faq/PerceptualDataAnnotationFAQ.jsx

const perceptualDataAnnotationFaqItems = [
  {
    question: 'What is Perceptual Data Annotation?',
    answer:
      'Perceptual Data Annotation is the generation of structured human-perceptual data for voice datasets through defined constructs, evaluation protocols, evaluator workflows, multirater judgments, quality monitoring, and reusable outputs.',
  },
  {
    question: 'How is this different from generic data labeling?',
    answer:
      'All-Voiced does not treat voice annotation as generic audio labeling. The work is organized around perceptual constructs, evaluator expertise, task design, agreement, variability, uncertainty, governance, and scientifically interpretable outputs.',
  },
  {
    question: 'What kinds of voice datasets can be annotated?',
    answer:
      'Projects may involve research datasets, clinical voice collections, curated reference sets, technology-evaluation datasets, multilingual collections, or other voice datasets where the owner has the rights and ethical basis for the proposed annotation use.',
  },
  {
    question: 'What perceptual attributes or constructs can be annotated?',
    answer:
      'Constructs may include voice-quality attributes, severity, similarity, naturalness, intelligibility, vocal effort, perceived change, category membership, or other project-defined perceptual constructs where methodologically appropriate and technically feasible.',
  },
  {
    question: 'Who provides the evaluators?',
    answer:
      'Evaluators may be provided by the institution, research team, project partner, or an agreed evaluator panel. All-Voiced does not automatically supply evaluators. Recruitment, panel coordination, access, preparation, and assignment responsibilities depend on project scope.',
  },
  {
    question: 'Can projects include multiple evaluator groups?',
    answer:
      'Potentially. Evaluator groups may differ by expertise, profession, language, institution, training background, or another characteristic relevant to the annotation objective. The grouping model should be defined in the annotation protocol.',
  },
  {
    question: 'Do evaluators need training or calibration?',
    answer:
      'Not every project requires the same preparation model. Familiarization, training, calibration, qualification, or repeated-item criteria should be defined according to the annotation objective and protocol. These outcomes should not be inferred from participation alone.',
  },
  {
    question: 'How many evaluators are needed per sample?',
    answer:
      'There is no universal number. It depends on the perceptual construct, sample variability, evaluator expertise, intended analysis, required precision, available resources, and downstream use.',
  },
  {
    question: 'Can annotation workflows be blinded?',
    answer:
      'Blinding-related workflows may be supported through controlled labels, ordering, access, and presentation conditions. The required blinding model must be defined and verified with the project team.',
  },
  {
    question: 'Can All-Voiced support repeated ratings or reliability checks?',
    answer:
      'Repeated items, repeated phases, completion monitoring, and reliability-related indicators may be included where supported by the configured workflow and explicitly defined in the project scope.',
  },
  {
    question: 'How are disagreement and uncertainty handled?',
    answer:
      'Disagreement can be represented as part of the data rather than treated only as error. Depending on the project, outputs may preserve individual judgments, distributions, variability, subgroup differences, uncertainty, or aggregate summaries. The appropriate representation should be defined according to the scientific or operational objective.',
  },
  {
    question: 'Do projects always produce a single consensus label?',
    answer:
      'No. Some projects require a consensus or aggregate reference, while others should preserve individual judgments, distributions, disagreement, subgroup patterns, or uncertainty.',
  },
  {
    question: 'What outputs can be delivered?',
    answer:
      'Core outputs may include raw evaluator responses, sample identifiers, structured ratings or categories, completion records, annotation protocols, data dictionaries, and structured exports. Aggregate summaries, dashboards, reports, evaluator metadata, consensus values, variability summaries, and reliability-related indicators may be included where appropriate and explicitly agreed.',
  },
  {
    question: 'Can the data be used for AI model development or validation?',
    answer:
      'Potentially, where dataset rights, consent, governance, and project scope allow it. Perceptual annotations may contribute to model development, benchmarking, validation, error analysis, or human-centered evaluation, but suitability depends on the annotation design and downstream use.',
  },
  {
    question: 'How are consent, privacy, and data rights handled?',
    answer:
      'Dataset owners remain responsible for rights, permissions, consent compatibility, and the ethical basis for the proposed use unless responsibilities are explicitly defined otherwise. Annotation workflows should address privacy, access, retention, reuse, publication, sharing, and downstream AI use where relevant. Uploading or accessing a dataset does not automatically authorize annotation, sharing, commercial use, or AI-related reuse.',
  },
  {
    question: 'Is this the same as Perceptual Research Infrastructure?',
    answer:
      'No. Perceptual Data Annotation starts from a dataset and the need to generate a structured human-perceptual layer. Perceptual Research Infrastructure starts from a research protocol and a project-specific perceptual workflow. Some projects require both.',
  },
  {
    question: 'How is pricing determined?',
    answer:
      'Pricing depends on dataset size, number of constructs, evaluator workflow, repetitions, protocol complexity, support, outputs, governance requirements, and any custom implementation needed.',
  },
  {
    question: 'What information should we prepare before discussing a dataset?',
    answer:
      'Helpful starting points include dataset size, sample characteristics, ownership and consent information, intended use, target perceptual constructs, evaluator requirements, languages, desired outputs, timeline, and governance constraints.',
  },
];

export default function PerceptualDataAnnotationFAQ() {
  return (
    <section
      className="service-landing-section service-landing-faq"
      aria-labelledby="perceptual-data-annotation-faq-title"
    >
      <span className="services-label">
        FAQ
      </span>

      <h2 id="perceptual-data-annotation-faq-title">
        Perceptual Data Annotation FAQ
      </h2>

      <p>
        Common questions about generating structured human-perceptual data
        for voice datasets through defined protocols, evaluator workflows,
        multirater annotation, and governed outputs.
      </p>

      <div className="service-landing-faq-list">
        {perceptualDataAnnotationFaqItems.map((item) => (
          <details className="service-landing-faq-item" key={item.question}>
            <summary>
              <span>{item.question}</span>
            </summary>

            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}